import React, { useMemo, useState } from "react";
import { Alert, Button, Modal, Space, Tag, Tooltip, message } from "antd";
import { BarcodeOutlined, ExclamationCircleOutlined, LockOutlined, UnlockOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import urls from "urls";
import { parseDate, summarisePickDay } from "../../../functions/pickDay";
import { pickDaySteps } from "../../../functions/pickDaySteps";
import { findShortages, summariseShortages } from "../../../functions/shortages";
import { PickDaySteps } from "./PickDaySteps";

/** "vrijdag 2 oktober 2026", from a DD-MM-YYYY string, without dayjs' format parsing. */
const longDate = (value) => {
  const date = parseDate(value);
  return date ? dayjs(date.getTime() + date.getTimezoneOffset() * 60000).format("dddd D MMMM YYYY") : value;
};

/**
 * The pick day bar on the order overview (ported from the Picqer day panel, branch
 * picqer-koppeling): how the day stands, releasing and unlocking it, and composing a round.
 *
 * Releasing locks the day - its orders become the snapshot rounds are built from - so it always
 * confirms first. The counts come from the pick day's own orders (usePickDayOrders), which can
 * span more than the delivery date selected on the page.
 *
 * A read that failed is shown as failed, never as its empty default: a pick day that could not
 * be read is not "open", and rounds that could not be read are not "0 open".
 */
const PickDayPanel = ({ pickDay, deliveryDates, day, dayOrders, waves, activeWaveCount, wavesError, onCompose }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [lockWarning, setLockWarning] = useState(null);

  const stats = useMemo(() => summarisePickDay(dayOrders.orders), [dayOrders.orders]);
  const openShortages = useMemo(() => summariseShortages(findShortages(dayOrders.orders)).open, [dayOrders.orders]);
  const failedShops = Object.keys(dayOrders.errorsByShop || {});
  const coversMoreDays = (deliveryDates || []).length > 1;
  const dayUnknown = !!day.error;
  const hasNotice = !!lockWarning || failedShops.length > 0 || dayUnknown || !!wavesError;

  const run = async (action, success) => {
    setBusy(true);
    try {
      const result = await action();
      if (result && result.ok === false) {
        message.error(
          result.reason === "pick-day/active-waves"
            ? t("orderpicking.day.error_active_waves", { count: result.count })
            : t("orderpicking.day.error_generic"),
          6,
        );
        return;
      }
      if (result && result.locks && !result.locks.HVO) setLockWarning(t("orderpicking.day.hvo_lock_missing"));
      else setLockWarning(null);
      message.success(success, 4);
    } catch (error) {
      message.error(error.message, 6);
    } finally {
      setBusy(false);
    }
  };

  const confirmRelease = () =>
    Modal.confirm({
      title: t("orderpicking.day.release_title", { day: longDate(pickDay) }),
      icon: <ExclamationCircleOutlined />,
      width: 560,
      content: (
        <div>
          <p>
            {t("orderpicking.day.release_body", { count: stats.total })}
            {coversMoreDays && ` ${t("orderpicking.day.release_covers", { dates: deliveryDates.join(", ") })}`}
          </p>
          <p>{t("orderpicking.day.release_lock")}</p>
          {failedShops.length > 0 && (
            <Alert type="warning" showIcon message={t("orderpicking.day.shops_not_loaded", { shops: failedShops.join(", ") })} />
          )}
        </div>
      ),
      okText: t("orderpicking.day.release"),
      cancelText: t("cancel"),
      onOk: () => run(() => day.release({ orderCount: stats.total }), t("orderpicking.day.released")),
    });

  const confirmUnlock = () =>
    Modal.confirm({
      title: t("orderpicking.day.unlock_title"),
      icon: <ExclamationCircleOutlined />,
      content: <p>{t("orderpicking.day.unlock_body")}</p>,
      okText: t("orderpicking.day.unlock"),
      okButtonProps: { danger: true },
      cancelText: t("cancel"),
      // unlock() reads the day's rounds itself; the count on screen may not have loaded
      onOk: () => run(() => day.unlock(), t("orderpicking.day.unlocked")),
    });

  const releasedBy = day.releasedBy && (day.releasedBy.email || day.releasedBy.uid);

  // The stappenlijst above the bar: on this screen its buttons are the bar's own release and compose
  const canRelease = !day.locked && !dayUnknown && !day.isLoading && stats.total > 0 && dayOrders.isLoaded;
  const steps =
    !dayUnknown && !day.isLoading && dayOrders.isLoaded
      ? pickDaySteps({ locked: !!day.locked, total: stats.total, unassigned: stats.unassigned, waves, openShortages })
      : null;

  return (
    <>
      {!!steps && (
        <PickDaySteps
          pickDay={pickDay}
          deliveryDate={(deliveryDates || [])[0]}
          model={steps}
          page="orders"
          actions={{
            release: canRelease ? confirmRelease : undefined,
            compose: day.locked && stats.unassigned > 0 ? onCompose : undefined,
          }}
        />
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          padding: "10px 12px",
          marginBottom: hasNotice ? 8 : 16,
          borderRadius: 4,
          border: `1px solid ${day.locked ? "#ffd591" : "#d9d9d9"}`,
          backgroundColor: day.locked ? "#fff7e6" : "#fafafa",
        }}
      >
        <span style={{ fontWeight: 600, minWidth: 110 }}>{t("orderpicking.day.label")}</span>
        {dayUnknown ? (
          <Tag icon={<ExclamationCircleOutlined />} color="red">
            {t("orderpicking.day.unknown_tag", { day: pickDay })}
          </Tag>
        ) : day.locked ? (
          <Tag icon={<LockOutlined />} color="orange">
            {t("orderpicking.day.locked_tag", { day: pickDay })}
          </Tag>
        ) : (
          <Tag>{t("orderpicking.day.open_tag", { day: pickDay })}</Tag>
        )}
        {coversMoreDays && (
          <Tooltip title={t("orderpicking.day.covers_tooltip", { dates: deliveryDates.join(", ") })}>
            <Tag color="blue">{t("orderpicking.day.covers", { count: deliveryDates.length })}</Tag>
          </Tooltip>
        )}
        <span style={{ fontSize: 13, color: "#595959" }}>
          {t("orderpicking.day.counts", { total: stats.total, unassigned: stats.unassigned })}
        </span>
        {stats.queued + stats.picking > 0 && <Tag color="blue">{t("orderpicking.day.in_rounds", { count: stats.queued + stats.picking })}</Tag>}
        {stats.picked > 0 && <Tag color="green">{t("orderpicking.day.picked", { count: stats.picked })}</Tag>}
        {stats.partial + stats.aborted > 0 && (
          <Link to={`${urls.shortages}?pickDay=${pickDay}`} title={t("orderpicking.day.shortages_link")}>
            {stats.partial > 0 && <Tag color="orange">{t("orderpicking.day.partial", { count: stats.partial })}</Tag>}
            {stats.aborted > 0 && <Tag color="red">{t("orderpicking.day.aborted", { count: stats.aborted })}</Tag>}
          </Link>
        )}
        {!!day.releasedAt && (
          <span style={{ fontSize: 12, color: "#8c8c8c" }}>
            {t("orderpicking.day.released_at", { when: dayjs(day.releasedAt).format("D MMM HH:mm"), who: releasedBy || "-" })}
          </span>
        )}

        <Space style={{ marginLeft: "auto" }} wrap>
          <Link to={`${urls.pickWaves}?pickDay=${pickDay}`}>
            <Button icon={<UnorderedListOutlined />}>
              {wavesError ? t("orderpicking.day.rounds_link_failed") : t("orderpicking.day.rounds_link", { count: activeWaveCount })}
            </Button>
          </Link>
          {day.locked ? (
            <>
              <Button icon={<UnlockOutlined />} loading={busy} onClick={confirmUnlock}>
                {t("orderpicking.day.unlock")}
              </Button>
              <Button type="primary" icon={<BarcodeOutlined />} disabled={stats.unassigned === 0} onClick={onCompose}>
                {t("orderpicking.day.compose", { count: stats.unassigned })}
              </Button>
            </>
          ) : (
            <Tooltip title={t("orderpicking.day.compose_needs_release")}>
              <Button
                type="primary"
                icon={<LockOutlined />}
                loading={busy}
                disabled={dayUnknown || day.isLoading || stats.total === 0 || !dayOrders.isLoaded}
                onClick={confirmRelease}
              >
                {t("orderpicking.day.release")}
              </Button>
            </Tooltip>
          )}
        </Space>
      </div>
      {dayUnknown && (
        <Alert
          type="error"
          showIcon
          className="mb-3"
          message={t("orderpicking.day.day_not_loaded", { day: pickDay, message: day.error.message || String(day.error) })}
        />
      )}
      {!!wavesError && (
        <Alert
          type="error"
          showIcon
          className="mb-3"
          message={t("orderpicking.day.waves_not_loaded", { message: wavesError.message || String(wavesError) })}
        />
      )}
      {failedShops.length > 0 && (
        <Alert
          type="error"
          showIcon
          className="mb-3"
          message={t("orderpicking.day.shops_not_loaded", { shops: failedShops.join(", ") })}
        />
      )}
      {!!lockWarning && <Alert type="warning" showIcon closable className="mb-3" message={lockWarning} onClose={() => setLockWarning(null)} />}
    </>
  );
};

export default PickDayPanel;
